import React from "react";
import { Autocomplete, TextField } from "@mui/material";

/**
 * 공통 드롭다운(ComboBox) — 시스템 표준 드롭다운. MUI Autocomplete 기반으로 통일한다.
 * (MUI Select 대신 이걸 써서 타이핑 검색 + 통일된 디자인을 얻는다)
 *
 * ★ 팝업/항목/선택색 등 드롭다운 "디자인"은 여기서 손대지 않는다.
 *   시스템 전체 드롭다운 디자인은 styles/muiTheme.js 의 MuiAutocomplete/MuiMenuItem
 *   styleOverrides(공통 dropdownPaper·dropdownItem) 한 곳에서 관리한다.
 *
 * 두 가지 옵션 형태를 지원:
 *   1) 문자열 배열  options={["일반","고정비"]}      → value/onChange 는 문자열. freeSolo 면 직접 입력도 가능.
 *   2) 객체 배열    options={[{value, label}]}       → value 는 그 value(id), onChange(선택된 value)로 넘어온다. 선택형.
 *
 * @example  <ComboBox options={["일반","고정비"]} value={v} onChange={setV} freeSolo placeholder="일반 / 고정비" />
 * @example  <ComboBox options={depts.map(d => ({ value: d.deptId, label: d.deptName }))}
 *                      value={deptId} onChange={setDeptId} placeholder="선택 안 함" />
 */
export default function ComboBox({
    options = [], value, onChange,
    placeholder = "선택", freeSolo = false, clearable = true,
    fullWidth = true, size = "small", renderOption, sx, ...rest
}) {
    // 옵션이 비었을 때(로딩 실패·권한 없음·아직 안 옴) 문자열 모드로 떨어지면, freeSolo 가 아닌데도
    // onInputChange 가 타이핑한 글자를 그대로 onChange 로 흘려 보낸다 — id 를 받아야 할 값 자리에
    // 사람 이름이 들어앉는다(증명서 발급 화면에서 실제로 그랬다). 고를 게 없으면 고를 수 없어야 한다.
    const objectMode = options.some(o => o && typeof o === "object")
        || (options.length === 0 && !freeSolo);

    if (objectMode) {
        const selected = options.find(o => String(o.value) === String(value ?? "")) ?? null;
        return (
            <Autocomplete
                size={size} fullWidth={fullWidth}
                options={options}
                value={selected}
                getOptionLabel={(o) => (o && o.label != null ? String(o.label) : "")}
                isOptionEqualToValue={(o, v) => String(o?.value) === String(v?.value)}
                onChange={(_, o) => onChange(o ? o.value : "")}
                disableClearable={!clearable}
                renderOption={renderOption}
                renderInput={(p) => <TextField {...p} placeholder={placeholder} />}
                sx={sx}
                {...rest}
            />
        );
    }

    // 문자열 옵션 모드 — freeSolo 면 목록 밖 값도 입력 가능(거래내용 구분 등)
    return (
        <Autocomplete
            size={size} fullWidth={fullWidth} freeSolo={freeSolo}
            options={options}
            value={value ?? ""}
            onInputChange={(_, v) => onChange(v)}
            renderOption={renderOption}
            renderInput={(p) => <TextField {...p} placeholder={placeholder} />}
            sx={sx}
            {...rest}
        />
    );
}
